import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { createClient } from '@supabase/supabase-js';
import { Lock, Eye, EyeOff, CheckCircle, AlertTriangle, Loader2, Sun, Moon } from 'lucide-react';
import { useTheme } from '../../hooks/useTheme';
import { useTranslation } from 'react-i18next';
import SEO from '../../components/SEO';


import { translateAuthError } from '../../utils/authErrors';

const supabase = createClient(
    import.meta.env.VITE_PUBLIC_SUPABASE_URL,
    import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY
);

export default function ResetPassword() {
    const { t } = useTranslation();
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);
    const { theme, toggleTheme } = useTheme();
    const navigate = useNavigate();

    useEffect(() => {
        // Écouter les changements d'état d'authentification
        // Supabase échange le token de l'URL contre une session automatiquement
        const { data: authListener } = supabase.auth.onAuthStateChange(async (event, session) => {
            console.log('RESET PASSWORD EVENT:', event); // Debug

            if (event === 'PASSWORD_RECOVERY') {
                // Session récupérée avec succès pour le reset
                setError('');
            } else if (event === 'SIGNED_IN') {
                // L'utilisateur est connecté, on peut reset
                setError('');
            } else if (event === 'SIGNED_OUT') {
                // On attend de voir si le token est traité
                // Mais si pas de session après un délai, c'est une erreur
            }
        });

        // Vérification initiale avec un léger délai pour laisser le temps au hash d'être traité
        const checkSession = async () => {
            // Petit délai pour laisser le temps à Supabase de process l'URL
            await new Promise(resolve => setTimeout(resolve, 500));
            const { data: { session } } = await supabase.auth.getSession();

            if (!session) {
                // On vérifie le hash pour ne pas afficher d'erreur si l'utilisateur arrive juste
                const hash = window.location.hash;
                if (!hash || (!hash.includes('access_token') && !hash.includes('type=recovery'))) {
                    setError('Auth session missing! (Lien invalide ou expiré)');
                }
            } else {
                setError('');
            }
        };

        checkSession();

        return () => {
            authListener.subscription.unsubscribe();
        };
    }, []);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (password !== confirmPassword) {
            setError(t('auth.reset.error_match'));
            return;
        }

        if (password.length < 6) {
            setError(t('auth.reset.error_length'));
            return;
        }

        setLoading(true);
        setError('');

        try {
            const { error } = await supabase.auth.updateUser({
                password: password,
            });

            if (error) throw error;

            setSuccess(true);
            setTimeout(() => {
                navigate('/login');
            }, 2000);
        } catch (err: any) {
            setError(translateAuthError(err.message));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-dark-bg transition-colors duration-300 flex items-center justify-center px-4 py-12 relative">
            <SEO
                title={t('auth.reset.title')}
                description={t('auth.reset.subtitle')}
                canonical="/reset-password"
            />
            <div className="absolute top-4 right-4">
                <button
                    onClick={toggleTheme}
                    className="p-2.5 rounded-xl text-text-muted hover:bg-dark-hover hover:text-text-main transition-colors"
                    title={theme === 'dark' ? t('dashboard.sidebar.mode_light') || 'Mode clair' : t('dashboard.sidebar.mode_dark') || 'Mode sombre'}
                >
                    {theme === 'dark' ? <Sun size={20} /> : <Moon size={20} />}
                </button>
            </div>

            <div className="w-full max-w-md">
                <div className="text-center mb-8">
                    <div className="inline-flex items-center gap-3 mb-6">
                        <div className="w-12 h-12 shrink-0">
                            <img src="/logo/app-logo.png" alt="Logo" className="w-full h-full object-contain" />
                        </div>
                        <span className="text-3xl font-bold text-text-main">CitoyenPro</span>
                    </div>
                    <h1 className="text-3xl font-bold text-text-main mb-2">{t('auth.reset.welcome')}</h1>
                    <p className="text-text-muted">{t('auth.reset.subtitle')}</p>
                </div>

                <div className="bg-dark-card rounded-2xl border border-dark-border p-8 shadow-xl">
                    {error && (
                        <div className="mb-6 p-4 bg-red-500/10 border border-red-500/20 rounded-xl">
                            <p className="text-sm text-red-500 flex items-center gap-2">
                                <AlertTriangle size={16} />
                                {error}
                            </p>
                        </div>
                    )}

                    {success ? (
                        <div className="text-center">
                            <div className="w-16 h-16 bg-green-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
                                <CheckCircle className="text-green-500" size={32} />
                            </div>
                            <h2 className="text-xl font-bold text-text-main mb-2">{t('settings.security.success')}</h2>
                            <p className="text-text-muted">
                                {t('auth.reset.updating')}...
                            </p>
                        </div>
                    ) : (
                        <form onSubmit={handleSubmit} className="space-y-6">
                            <div>
                                <label htmlFor="password" className="block text-sm font-semibold text-text-main mb-2">
                                    {t('auth.reset.new_password')}
                                </label>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                                        <Lock className="text-text-muted" size={20} />
                                    </div>
                                    <input
                                        type={showPassword ? 'text' : 'password'}
                                        id="password"
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        className="w-full pl-12 pr-12 py-3 bg-dark-bg border border-dark-border rounded-xl focus:border-primary focus:outline-none transition-colors text-sm text-text-main placeholder-text-muted"
                                        placeholder="••••••••"
                                        required
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowPassword(!showPassword)}
                                        className="absolute inset-y-0 right-0 pr-4 flex items-center cursor-pointer"
                                    >
                                        {showPassword ? <EyeOff className="text-text-muted" size={20} /> : <Eye className="text-text-muted" size={20} />}
                                    </button>
                                </div>
                            </div>

                            <div>
                                <label htmlFor="confirmPassword" className="block text-sm font-semibold text-text-main mb-2">
                                    {t('auth.reset.confirm_password')}
                                </label>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                                        <Lock className="text-text-muted" size={20} />
                                    </div>
                                    <input
                                        type={showPassword ? 'text' : 'password'}
                                        id="confirmPassword"
                                        value={confirmPassword}
                                        onChange={(e) => setConfirmPassword(e.target.value)}
                                        className="w-full pl-12 pr-4 py-3 bg-dark-bg border border-dark-border rounded-xl focus:border-primary focus:outline-none transition-colors text-sm text-text-main placeholder-text-muted"
                                        placeholder="••••••••"
                                        required
                                    />
                                </div>
                            </div>

                            <button
                                type="submit"
                                disabled={loading || error.includes('invalide')}
                                className="w-full py-3 bg-primary text-white font-bold rounded-xl hover:bg-primary-hover transition-colors shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                            >
                                {loading ? (
                                    <>
                                        <Loader2 className="animate-spin" size={20} />
                                        {t('auth.reset.updating')}
                                    </>
                                ) : (
                                    t('auth.reset.submit')
                                )}
                            </button>
                        </form>
                    )}
                </div>
            </div>
        </div>
    );
}
